import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { NetworkConnection, ConnectionStatusEnum } from "@/types/connection";
import {
  getInterviewHubConfig,
  generateAgentNameFromUserInfo,
} from "@/config/interviewHubConfig";
import {
  saveAgentNameForNetwork,
} from "@/utils/cookies";
import openAgentsLogo from "@/assets/images/open-agents-logo.png";
import { ReactComponent as PeakMojoLogo } from "@/assets/images/peakmojo.svg";

/**
 * Read file as Base64 string
 * @param file - File to read
 * @returns Base64 encoded string (without data URL prefix)
 */
const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data:application/pdf;base64, prefix
      const base64Content = base64.split(",")[1];
      resolve(base64Content);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Interview Hub Login Component
 *
 * Simplified login page for Interview Hub mode that collects
 * user information (first name, last name, email, resume PDF) and automatically
 * connects to the configured network.
 */
const InterviewHubLogin: React.FC = () => {
  const navigate = useNavigate();
  const { setAgentName, handleNetworkSelected, setPasswordHash } = useAuthStore();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeBase64, setResumeBase64] = useState<string>("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = getInterviewHubConfig();

  const handleResumeSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");

    // Validate file type
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a PDF file");
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Resume file must be less than 10MB");
      return;
    }

    try {
      const base64Content = await readFileAsBase64(file);
      setResumeFile(file);
      setResumeBase64(base64Content);
    } catch (err) {
      console.error("Failed to read file:", err);
      setError("Failed to read the resume file. Please try again.");
    }
  };

  const handleRemoveResume = () => {
    setResumeFile(null);
    setResumeBase64("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate inputs
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("Please fill in all fields");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Validate resume
    if (!resumeFile || !resumeBase64) {
      setError("Please upload your resume (PDF)");
      return;
    }

    setIsConnecting(true);

    try {
      // Generate agent name from user info
      const agentName = generateAgentNameFromUserInfo(firstName, lastName);
      console.log("🎯 Generated agent name:", agentName);

      // Create network connection object
      const networkConnection: NetworkConnection = {
        host: config.host,
        port: config.port,
        status: ConnectionStatusEnum.CONNECTING,
        networkInfo: {
          name: "Interview Hub",
        },
      };
      console.log("🌐 Created network connection:", networkConnection);

      // Save the connection and agent name
      handleNetworkSelected(networkConnection);
      setAgentName(agentName);
      console.log("✅ Saved network and agent name to auth store");

      // Save agent name to cookies
      saveAgentNameForNetwork(config.host, config.port, agentName);
      console.log("🍪 Saved agent name to cookies");

      // Save user info and resume to localStorage for auto-registration + verification
      const userInfo = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        resume_filename: resumeFile.name,
        resume_base64: resumeBase64,
        resume_size: resumeFile.size,
      };
      localStorage.setItem('interview_hub_user_info', JSON.stringify(userInfo));
      console.log("💾 Saved user info with resume to localStorage");

      // No password for interview hub mode
      setPasswordHash(null);

      // Navigate directly to the interview page
      console.log("🚀 Navigating to /interview");
      navigate("/interview");
    } catch (err) {
      console.error("Failed to connect:", err);
      setError("Failed to connect to Interview Hub. Please try again.");
      setIsConnecting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5 bg-gradient-to-br from-indigo-400 to-purple-500 dark:from-blue-800 dark:to-violet-800">
      <div className="max-w-lg w-full text-center rounded-2xl p-10 bg-white shadow-2xl shadow-black/25 dark:bg-gray-800 dark:shadow-black/50">
        {/* Header */}
        <div className="mb-6">
          {/* Logo */}
          <div className="w-20 h-20 mx-auto mb-4">
            <PeakMojoLogo className="w-full h-full" />
          </div>

          <h1 className="text-3xl font-bold mb-3 text-gray-800 dark:text-gray-50">
            Peak Mojo AI Interview Hub
          </h1>
          <p className="text-base leading-relaxed text-gray-500 dark:text-gray-300">
            Please enter your information to get started with your interview journey.
          </p>
        </div>

        {/* Network Info */}
        <div className="rounded-xl p-4 mt-4 mb-4 text-left bg-gray-100 dark:bg-gray-700">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Connecting to
          </div>
          <div className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {config.host}:{config.port}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="my-4">
          {/* First Name */}
          <div className="mb-4 text-left">
            <label
              htmlFor="firstName"
              className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-150 focus:outline-none focus:ring-3 bg-white border-gray-300 text-gray-800 focus:border-blue-500 focus:ring-blue-500/10 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-50 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
              placeholder="Enter your first name"
              maxLength={50}
              autoComplete="given-name"
              disabled={isConnecting}
            />
          </div>

          {/* Last Name */}
          <div className="mb-4 text-left">
            <label
              htmlFor="lastName"
              className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300"
            >
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-150 focus:outline-none focus:ring-3 bg-white border-gray-300 text-gray-800 focus:border-blue-500 focus:ring-blue-500/10 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-50 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
              placeholder="Enter your last name"
              maxLength={50}
              autoComplete="family-name"
              disabled={isConnecting}
            />
          </div>

          {/* Email */}
          <div className="mb-4 text-left">
            <label
              htmlFor="email"
              className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300"
            >
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-150 focus:outline-none focus:ring-3 bg-white border-gray-300 text-gray-800 focus:border-blue-500 focus:ring-blue-500/10 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-50 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
              placeholder="Enter your email address"
              maxLength={100}
              autoComplete="email"
              disabled={isConnecting}
            />
          </div>

          {/* Resume Upload */}
          <div className="mb-4 text-left">
            <label
              htmlFor="resume"
              className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300"
            >
              Resume (PDF) <span className="text-red-500">*</span>
            </label>
            <input
              ref={fileInputRef}
              id="resume"
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleResumeSelect}
              className="hidden"
              disabled={isConnecting}
            />
            {!resumeFile ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isConnecting}
                className="w-full px-4 py-3 border-2 border-dashed rounded-lg text-base transition-all duration-150 focus:outline-none focus:ring-3 bg-white border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 focus:border-blue-500 focus:ring-blue-500/10 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-400 dark:hover:border-blue-400 dark:hover:text-blue-400 dark:focus:border-blue-400 dark:focus:ring-blue-400/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Click to upload your resume</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">PDF format, max 10MB</div>
              </button>
            ) : (
              <div className="w-full px-4 py-3 border-2 rounded-lg bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium truncate max-w-[200px]">{resumeFile.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({(resumeFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveResume}
                    disabled={isConnecting}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="text-red-500 dark:text-red-400 text-sm flex items-start gap-2">
                <span className="mt-0.5">⚠️</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Agent Name Preview */}
          {firstName && lastName && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-left">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-400">
                Your Agent Name
              </div>
              <div className="text-base font-semibold text-blue-900 dark:text-blue-100 mt-1">
                {generateAgentNameFromUserInfo(firstName, lastName)}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!firstName.trim() || !lastName.trim() || !email.trim() || !resumeFile || isConnecting}
            className={`w-full px-6 py-3 border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-150 text-white ${
              !firstName.trim() || !lastName.trim() || !email.trim() || !resumeFile || isConnecting
                ? "bg-gray-300 dark:bg-gray-500 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/30"
            }`}
          >
            {isConnecting ? (
              <div className="flex justify-center items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Connecting...</span>
              </div>
            ) : (
              <div className="flex justify-center items-center gap-2">
                <span>Get Started</span>
                <span>→</span>
              </div>
            )}
          </button>
        </form>

        {/* Footer Info */}
        <div className="mt-6 text-xs text-gray-500 dark:text-gray-400">
          By continuing, you agree to share this information with the interview platform.
        </div>

        {/* Powered by */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <a
            href="https://github.com/openagents-org/openagents"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 text-base text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
          >
            <span>Powered by</span>
            <img src={openAgentsLogo} alt="OpenAgents" className="w-8 h-8" />
            <span className="text-lg font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              OpenAgents
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default InterviewHubLogin;
